import { z } from 'zod';

import { BooleanParamSchema } from '../common/boolean-param/boolean-param';
import { UuidSchema } from '../common/id/id';
import { makeListQuery } from '../common/list-query/list-query';
import { makeTranslationsSchema } from '../common/translations/translations';
import { QuestionVisibilityPolicySchema } from '../visibility/resolve-visibility/resolve-visibility';
import { VisibilityModeSchema } from '../visibility/visibility-mode/visibility-mode';

import { QuestionAudienceSchema } from './question-audience/question-audience';
import { QuestionIdentityRoleSchema } from './question-identity-role/question-identity-role';
import { QuestionOptionSchema, QuestionOptionDetailSchema } from './question-option';
import { QuestionTypeSchema } from './question-type/question-type';
import { StableKeySchema } from './stable-key/stable-key';

/**
 * A staff-authored setting key this question's **required-ness** follows, or
 * `null` for a question that is simply required or not.
 *
 * This is how gender-dependent requiredness stays out of code (U15): the seeded
 * Civil Registry ID question binds to `registration.civilRegistryIdRequired`,
 * which is a **gender-scoped** boolean, so "required for men, optional for
 * women" is a settings write rather than an `if (gender === …)` branch — and any
 * question can do the same.
 *
 * A plain string here rather than the api's `SettingKey` union: contracts cannot
 * import from an app. The api validates it against the live settings registry
 * when the question is written, so an unknown key is a 422 at the builder, never
 * a silent fallback at form time.
 */
export const RequiredSettingKeySchema = z
  .string()
  .min(3)
  .max(120)
  .regex(/^[a-z][a-zA-Z0-9]*(?:\.[a-z][a-zA-Z0-9]*)+$/, 'Expected a dotted settings key.');

/**
 * One question, resolved into the reader's locale and (on member surfaces)
 * against the reader's gender.
 *
 * The four visibility columns travel with it because the mobile privacy screen
 * and the admin builder both need the policy alongside the question — the
 * resolver's inputs and the question are the same object, so they can never be
 * fetched out of step.
 */
export const ProfileQuestionSchema = z
  .object({
    id: UuidSchema,
    sectionId: UuidSchema,
    key: StableKeySchema,
    type: QuestionTypeSchema,
    audience: QuestionAudienceSchema,
    /**
     * The **effective** requiredness. On a composed member form this is already
     * resolved: if `requiredSettingKey` is set, it holds the gender-scoped
     * setting's value, not the stored column. Clients never resolve it again.
     */
    isRequired: z.boolean(),
    requiredSettingKey: RequiredSettingKeySchema.nullable(),
    isActive: z.boolean(),
    /** Offered as a browse/search filter (P17). */
    isFilterable: z.boolean(),
    /** Fed into the nomination score (P16). */
    isMatchable: z.boolean(),
    /**
     * Editing this answer after approval sends the member back for review (D6):
     * the save opens a `re_review` verification case and gates the account until
     * staff decide. Off, an approved member edits the answer freely.
     */
    requiresReviewOnEdit: z.boolean(),
    /**
     * Which line of a member's introduction this answer is — `headline`,
     * `subline`, or `null` for an ordinary fact (`question-identity-role.ts`).
     */
    identityRole: QuestionIdentityRoleSchema.nullable(),
    /** The staff-authored constraints; shape depends on `type` (`question-validation.ts`). */
    validation: z.record(z.string(), z.unknown()),
    sortOrder: z.number().int().min(0),

    // The visibility policy (A3) — columns on the question, not a policy table.
    publicVisibility: VisibilityModeSchema,
    matchedVisibility: VisibilityModeSchema,
    defaultVisiblePublic: z.boolean(),
    defaultVisibleMatched: z.boolean(),

    /** The active locale's copy. */
    label: z.string(),
    helpText: z.string().nullable(),
    placeholder: z.string().nullable(),

    /** Empty for every type that is not option-backed (`isOptionBacked`). */
    options: z.array(QuestionOptionSchema),
  })
  .strict();

export type ProfileQuestion = z.infer<typeof ProfileQuestionSchema>;

export const ProfileQuestionTranslationsSchema = makeTranslationsSchema(
  z
    .object({
      label: z.string().min(1).max(300),
      helpText: z.string().max(2_000).nullable(),
      placeholder: z.string().max(200).nullable(),
    })
    .strict(),
);

export type ProfileQuestionTranslations = z.infer<typeof ProfileQuestionTranslationsSchema>;

/**
 * A question as the **builder's list** sees it: the question, plus the one
 * operational fact staff need before they touch it.
 *
 * A separate schema rather than a field on `ProfileQuestionSchema`, because that
 * schema is also the member-facing one — `ComposedFormSectionSchema` is built
 * from it. Putting a count of other members' answers there would ship an
 * operational statistic inside the most-cached read in the product, and force the
 * form composer to ask the QueryBus for a number no member will ever see.
 */
export const AdminProfileQuestionSchema = ProfileQuestionSchema.extend({
  /** How many members have answered it — what the delete confirmation names. */
  answerCount: z.number().int().min(0),
}).strict();

export type AdminProfileQuestion = z.infer<typeof AdminProfileQuestionSchema>;

/** A question plus every locale and its full options — the builder's edit form (W7). */
export const ProfileQuestionDetailSchema = AdminProfileQuestionSchema.omit({ options: true })
  .extend({
    translations: ProfileQuestionTranslationsSchema,
    options: z.array(QuestionOptionDetailSchema),
  })
  .strict();

export type ProfileQuestionDetail = z.infer<typeof ProfileQuestionDetailSchema>;

/**
 * Creating a question.
 *
 * The visibility policy is **required, not defaulted**: a question whose policy
 * the platform guessed is a field whose exposure nobody decided, and the honest
 * failure is a builder form that will not submit until staff have said what this
 * field is (`03-visibility-policy.md` gives the admin matrix plain-language
 * helper text for exactly this moment).
 *
 * `sortOrder` is **absent by construction**, like `key` and `type` on the update.
 * A new question is appended, and its position is thereafter changed only through
 * `ReorderItemsSchema` or `MoveQuestionSchema` — the two writes that compute every
 * position from a set the server holds. That is what makes `MoveQuestionSchema`'s
 * claim ("a client never sends a position") true, and it is load-bearing: the
 * positional write's lock argues that a question created while it holds a section
 * takes `max + 1` and therefore still sorts last, which a caller-chosen `0` would
 * silently falsify.
 */
export const CreateProfileQuestionSchema = z
  .object({
    sectionId: UuidSchema,
    key: StableKeySchema,
    type: QuestionTypeSchema,
    audience: QuestionAudienceSchema.optional(),
    isRequired: z.boolean().optional(),
    requiredSettingKey: RequiredSettingKeySchema.nullable().optional(),
    isActive: z.boolean().optional(),
    isFilterable: z.boolean().optional(),
    isMatchable: z.boolean().optional(),
    requiresReviewOnEdit: z.boolean().optional(),
    identityRole: QuestionIdentityRoleSchema.nullable().optional(),
    validation: z.record(z.string(), z.unknown()).optional(),
    visibility: QuestionVisibilityPolicySchema,
    translations: ProfileQuestionTranslationsSchema,
    /** Created with the question, so an option-backed type is never saved empty. */
    options: z
      .array(
        z
          .object({
            value: StableKeySchema,
            sortOrder: z.number().int().min(0).optional(),
            translations: makeTranslationsSchema(
              z.object({ label: z.string().min(1).max(200) }).strict(),
            ),
          })
          .strict(),
      )
      .max(200)
      .optional(),
  })
  .strict();

export type CreateProfileQuestionRequest = z.infer<typeof CreateProfileQuestionSchema>;

/**
 * Updating a question. `key` and **`type`** are both absent by construction:
 * the key is the answer's address, and the type is the shape every stored answer
 * already has — changing it would leave a column of values that the new
 * validator rejects and no migration can repair (a `multi_select` array read as
 * a `date`). Staff deactivate and replace.
 *
 * `sortOrder` is absent for the reason `CreateProfileQuestionSchema` gives:
 * position is written only by the two endpoints that compute it from a set the
 * server holds. `sectionId` **stays** — moving a question between sections
 * through this form is an edit, and it lands it at the end of its new section,
 * which is where `MoveQuestionSchema` exists to change.
 */
export const UpdateProfileQuestionSchema = z
  .object({
    sectionId: UuidSchema.optional(),
    audience: QuestionAudienceSchema.optional(),
    isRequired: z.boolean().optional(),
    requiredSettingKey: RequiredSettingKeySchema.nullable().optional(),
    isActive: z.boolean().optional(),
    isFilterable: z.boolean().optional(),
    isMatchable: z.boolean().optional(),
    requiresReviewOnEdit: z.boolean().optional(),
    identityRole: QuestionIdentityRoleSchema.nullable().optional(),
    validation: z.record(z.string(), z.unknown()).optional(),
    visibility: QuestionVisibilityPolicySchema.optional(),
    translations: ProfileQuestionTranslationsSchema.optional(),
  })
  .strict();

export type UpdateProfileQuestionRequest = z.infer<typeof UpdateProfileQuestionSchema>;

/** The whitelisted filters of the admin question list (N7). */
export const ProfileQuestionsListQuerySchema = makeListQuery({
  sectionId: UuidSchema.optional(),
  type: QuestionTypeSchema.optional(),
  audience: QuestionAudienceSchema.optional(),
  isActive: BooleanParamSchema.optional(),
  isFilterable: BooleanParamSchema.optional(),
  isMatchable: BooleanParamSchema.optional(),
  requiresReviewOnEdit: BooleanParamSchema.optional(),
  search: z.string().min(1).max(200).optional(),
});

export type ProfileQuestionsListQuery = z.infer<typeof ProfileQuestionsListQuerySchema>;
